import { useState } from "react";
import { studioAPI, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { MONEY } from "@/constants/testIds";
import { toast } from "sonner";
import { Wallet, ArrowRight, Lock, Zap, Check } from "lucide-react";

const TIERS = [
    { key: "0-1k",     min: 0,     max: 1000,   label: "0 → 1K",     goal_fr: "Trouver le format gagnant", goal_en: "Find your winning format" },
    { key: "1k-10k",   min: 1000,  max: 10000,  label: "1K → 10K",   goal_fr: "Régularité + vidéos longues", goal_en: "Consistency + long-form" },
    { key: "10k-50k",  min: 10000, max: 50000,  label: "10K → 50K",  goal_fr: "Affiliation + premiers sponsors", goal_en: "Affiliate + first sponsors" },
    { key: "50k-100k", min: 50000, max: 100000, label: "50K → 100K", goal_fr: "Produits + sponsors récurrents", goal_en: "Products + recurring sponsors" },
    { key: "100k+",    min: 100000,max: 10_000_000, label: "100K+",  goal_fr: "Marque + licensing", goal_en: "Brand + licensing" },
];

export default function Monetization() {
    const { lang } = useApp();
    const [subs, setSubs] = useState(2500);
    const [niche, setNiche] = useState(lang === "fr" ? "chat humour storytelling" : "cat humor storytelling");
    const [busy, setBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const parsed = tryParseJSON(raw);

    const currentTier = TIERS.find((t) => subs >= t.min && subs < t.max) || TIERS[TIERS.length - 1];
    const tierIndex = TIERS.indexOf(currentTier);

    const run = async () => {
        setBusy(true); setRaw("");
        try {
            const { result } = await studioAPI.monetization({ subscribers: subs, niche, language: lang });
            setRaw(result);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid={MONEY.container} className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div>
                        <div className="overline">06 · Monetization</div>
                        <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Ta feuille de route" : "Your roadmap"}</h2>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-slate-500">
                            {lang === "fr" ? "Abonnés actuels" : "Current subscribers"}: <span className="text-[#FF4D00] font-mono-sf">{subs.toLocaleString()}</span>
                        </label>
                        <input data-testid={MONEY.subscribers} type="range" min="0" max="500000" step="500" value={subs}
                            onChange={(e) => setSubs(+e.target.value)} className="w-full mt-2 accent-[#FF4D00]" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Ta niche" : "Your niche"}</label>
                        <input data-testid={MONEY.niche} value={niche} onChange={(e) => setNiche(e.target.value)}
                            className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                    </div>
                    <button data-testid={MONEY.generateBtn} onClick={run} disabled={busy}
                        className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60">
                        {busy ? "···" : <>{lang === "fr" ? "Générer ma roadmap" : "Generate my roadmap"} <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </div>

                {/* Tier progression */}
                <div className="bg-[#161D2A] border border-[#253046] rounded p-5">
                    <div className="overline">Progression</div>
                    <div className="mt-4 space-y-2">
                        {TIERS.map((t, i) => {
                            const unlocked = i <= tierIndex;
                            const active = i === tierIndex;
                            return (
                                <div key={t.key} className={`flex items-center gap-3 p-2.5 rounded border ${active ? "border-[#FF4D00] bg-[#FF4D00]/5" : "border-[#253046]"}`}>
                                    {unlocked ? <Check className="w-4 h-4 text-[#10B981]" /> : <Lock className="w-4 h-4 text-slate-600" />}
                                    <div className="flex-1">
                                        <div className="font-display text-sm font-bold uppercase">{t.label}</div>
                                        <div className="text-xs text-slate-400">{lang === "fr" ? t.goal_fr : t.goal_en}</div>
                                    </div>
                                    {active && <Zap className="w-4 h-4 text-[#FF4D00]" />}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div data-testid={MONEY.result} className="rounded border border-[#253046] bg-[#0B111B] p-5">
                {!raw && !busy && (
                    <div className="text-center py-16">
                        <Wallet className="w-10 h-10 text-slate-700 mb-3 mx-auto" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "La roadmap détaillée apparaîtra ici." : "The detailed roadmap will appear here."}</p>
                    </div>
                )}
                {busy && <div className="py-16 text-center text-slate-400 font-mono-sf text-xs">CALCULATING REVENUE PATH ···</div>}
                {parsed && (
                    <div className="space-y-5">
                        {parsed.priorities && (
                            <div>
                                <div className="overline">This month · Priorities</div>
                                <div className="mt-2 space-y-2">
                                    {parsed.priorities.map((p, i) => (
                                        <div key={i} className="bg-[#161D2A] border-l-2 border-[#FF4D00] rounded-r p-3 text-sm">
                                            <span className="font-mono-sf text-[10px] text-[#FF4D00] mr-2">{String(i + 1).padStart(2, "0")}</span>
                                            {p}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {parsed.revenue_streams && (
                            <div>
                                <div className="overline">Revenue streams</div>
                                <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {parsed.revenue_streams.map((r, i) => (
                                        <div key={i} className="bg-[#161D2A] border border-[#253046] rounded p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="font-display text-sm font-bold uppercase">{r.name}</div>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono-sf ${r.status === "active" ? "bg-[#10B981]/20 text-[#10B981]" : r.status === "unlocking" ? "bg-[#FFB800]/20 text-[#FFB800]" : "bg-slate-800 text-slate-500"}`}>
                                                    {r.status}
                                                </span>
                                            </div>
                                            {r.monthly_estimate_eur && <div className="font-display text-xl font-black mt-2">€{r.monthly_estimate_eur}<span className="text-xs text-slate-500">/mo</span></div>}
                                            <div className="text-xs text-slate-400 mt-1">{r.action}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="grid sm:grid-cols-2 gap-3">
                            {parsed.next_milestone && <div className="bg-[#161D2A] border border-[#253046] rounded p-3"><div className="overline">Next milestone</div><div className="text-sm mt-1 text-slate-200">{parsed.next_milestone}</div></div>}
                            {parsed.long_term_vision && <div className="bg-[#161D2A] border border-[#253046] rounded p-3"><div className="overline">Long-term vision</div><div className="text-sm mt-1 text-slate-200">{parsed.long_term_vision}</div></div>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
